import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import { useNavigate, Link } from 'react-router-dom';
import background from '../../assets/Background_Test.jpg';

const PATREON_AUTH_URL = 'https://us-central1-mygmtracker.cloudfunctions.net/patreonAuth';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundImage: 'url(' + background + ')',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Monda', sans-serif",
    position: 'relative',
  },
  overlay: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 2,
    background: 'rgba(10,10,10,0.82)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 8px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05)',
    display: 'flex',
    gap: '32px',
  },
  left: { flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' },
  title: {
    fontFamily: "'Anton', sans-serif",
    fontSize: '28px', letterSpacing: '3px',
    color: '#fff', textAlign: 'center',
    marginBottom: '8px',
    textShadow: '0 0 30px rgba(200,0,0,0.4)',
  },
  label: { color: 'rgba(255,255,255,0.5)', fontSize: '11px', letterSpacing: '1px', fontFamily: "'Monda', sans-serif", marginBottom: '4px' },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '4px', padding: '10px 12px',
    color: '#fff', fontSize: '14px', outline: 'none',
    fontFamily: "'Monda', sans-serif",
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  btnRow: { display: 'flex', gap: '10px', marginTop: '8px' },
  btnPrimary: {
    fontFamily: "'Monda', sans-serif", letterSpacing: '1px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.5)',
    color: '#fff', padding: '8px 18px', borderRadius: '4px',
    cursor: 'pointer', fontSize: '12px', transition: 'background 0.2s, border-color 0.2s, color 0.2s',
  },
  btnPatreon: {
    fontFamily: "'Monda', sans-serif", letterSpacing: '1px',
    background: '#fff', border: '1px solid #fff',
    color: '#052d49', padding: '10px', borderRadius: '4px',
    cursor: 'pointer', fontSize: '12px', transition: 'background 0.2s, border-color 0.2s, color 0.2s', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '10px',
    color: 'rgba(255,255,255,0.2)', fontSize: '11px',
    fontFamily: "'Monda', sans-serif",
  },
  dividerLine: {
    flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)',
  },
  error: { color: '#ff4444', fontSize: '12px', fontFamily: "'Monda', sans-serif" },
  success: { color: '#2ecc71', fontSize: '12px', fontFamily: "'Monda', sans-serif" },
  forgotLink: {
    color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: "'Monda', sans-serif",
    textAlign: 'right', cursor: 'pointer', textDecoration: 'underline', marginTop: '-8px',
  },
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch {
      setError('Email or password incorrect.');
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setResetMessage('');
    if (!email) {
      setError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      // Message volontairement générique : ne pas révéler si l'email existe ou non (sécurité)
      setResetMessage('If an account exists for this email, a reset link has been sent.');
    } catch (err) {
      if (err.code === 'auth/invalid-email') setError('Please enter a valid email address.');
      else setResetMessage('If an account exists for this email, a reset link has been sent.');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePatreonLogin = () => {
    // Sans UID car l'user n'est pas encore connecte
    // Le callback gerera la liaison si l'user existe deja
    window.location.href = PATREON_AUTH_URL + '?state=patreon_login';
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Imprima&family=Monda:wght@400;700&display=swap" rel="stylesheet" />
      <div style={styles.page}>
        <div style={styles.overlay} />
        <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginBottom: '0' }}>
          <div style={styles.title}>LOGIN</div>
          <div style={styles.card}>
            <div style={styles.left}>
              <div>
                <div style={styles.label}>EMAIL / USERNAME</div>
                <input style={styles.input} type="email" value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  onFocus={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                  onBlur={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
              </div>
              <div>
                <div style={styles.label}>PASSWORD</div>
                <input style={styles.input} type="password" value={password}
                  onChange={function(e) { setPassword(e.target.value); }}
                  onFocus={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                  onBlur={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
              </div>
              <div style={styles.forgotLink} onClick={resetLoading ? undefined : handleForgotPassword}>
                {resetLoading ? 'Sending...' : 'Forgot password?'}
              </div>
              {error && <div style={styles.error}>{error}</div>}
              {resetMessage && <div style={styles.success}>{resetMessage}</div>}
              <div style={styles.btnRow}>
                <button style={{
                  ...styles.btnPrimary,
                  background: hoveredBtn === 'login' ? 'rgba(255,255,255,0.1)' : 'transparent',
                  borderColor: hoveredBtn === 'login' ? '#fff' : 'rgba(255,255,255,0.5)',
                }}
                  onMouseEnter={function() { setHoveredBtn('login'); }}
                  onMouseLeave={function() { setHoveredBtn(null); }}
                  onClick={handleLogin}>LOGIN</button>
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <button style={{
                    ...styles.btnPrimary,
                    background: hoveredBtn === 'signup' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderColor: hoveredBtn === 'signup' ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}
                    onMouseEnter={function() { setHoveredBtn('signup'); }}
                    onMouseLeave={function() { setHoveredBtn(null); }}>SIGNUP</button>
                </Link>
              </div>

              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                OR
                <div style={styles.dividerLine} />
              </div>

              <button
                style={{ ...styles.btnPatreon, background: hoveredBtn === 'patreon' ? '#f0f0f0' : '#fff', borderColor: '#fff' }}
                onMouseEnter={function() { setHoveredBtn('patreon'); }}
                onMouseLeave={function() { setHoveredBtn(null); }}
                onClick={handlePatreonLogin}>
                <svg width="18" height="18" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2">
                  <g transform="matrix(.47407 0 0 .47407 .383 .422)">
                    <path d="M1033.05 324.45c-.19-137.9-107.59-250.92-233.6-291.7-156.48-50.64-362.86-43.3-512.28 27.2-181.1 85.46-237.99 272.66-240.11 459.36-1.74 153.5 13.58 557.79 241.62 560.67 169.44 2.15 194.67-216.18 273.07-321.33 55.78-74.81 127.6-95.94 216.01-117.82 151.95-37.61 255.51-157.53 255.29-316.38z" fill="#000000" fill-rule="nonzero"/>
                  </g>
                </svg>
                CONTINUE WITH PATREON
              </button>
            </div>
            <img
              src={process.env.PUBLIC_URL + '/logo-main.png'}
              alt="Logo"
              style={{ width: '160px', height: '160px', objectFit: 'contain', filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.15))' }}
              onError={function(e) { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
